import { useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { addDocument, getProcessById, checkSignCode } from '@/services/api'
import {
  getProcessId,
  getAvailableFreeSignatures,
  getTokenFromSession,
  saveProcessId,
  saveExpirationTime,
  getExpirationTime,
} from '@/services/session'
import { useConfig } from '@/services/config'
import { getConfig } from '../../../config'
import {
  validateFileForSignatureType,
  SIGNATURE_TYPES,
} from '@/utils/signatureTypes'
import {
  validateFileUpload,
  isFreeSignatureAvailable,
} from '@/utils/fileValidation'
import type { DocumentDto } from '@cencert/mob-signature'
import type { RectangleCoordinates } from '@enigma/fe-signature/models/sign-file.model'
import {
  AddDocumentDialogStep1,
  type Step1Data,
} from './AddDocumentDialogStep1'
import { AddDocumentDialogStep2 } from './AddDocumentDialogStep2'

interface AddDocumentDialogProps {
  isOpen: boolean
  onClose: () => void
  onAddDocuments: (fileId: string, fileSize: number) => void
  documents?: DocumentDto[]
  documentSizes?: Map<string, number>
}

export function AddDocumentDialog({
  isOpen,
  onClose,
  onAddDocuments,
  documents = [],
  documentSizes = new Map(),
}: AddDocumentDialogProps) {
  const { t } = useTranslation()
  const { setExpirationTime } = useConfig()
  const { graphicSignatureEnabled } = getConfig()
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [inputCodeError, setInputCodeError] = useState<string | null>(null)
  const [signatureCoordinates, setSignatureCoordinates] =
    useState<RectangleCoordinates | null>(null)

  const isFreeSignatureEnabled =
    isFreeSignatureAvailable(documents) && getAvailableFreeSignatures()

  const validateFile = (file: File): string | null => {
    if (!step1Data) return null

    // Use the new configuration-based validation
    const validationContext = {
      signatureType: step1Data.signatureType,
      currentDocuments: documents.map(doc => ({
        size: documentSizes.get(doc.id) ?? 0, // Use stored file size
        name: doc.name,
        ...(doc.code !== undefined ? { code: doc.code } : {}),
      })),
      t,
    }

    // Check configuration-based limits first
    const configValidationError = validateFileUpload(file, validationContext)
    if (configValidationError) {
      return configValidationError
    }

    // Then check signature type specific validation
    const signatureTypeToValidate =
      SIGNATURE_TYPES.find(
        type => type.documentSignFormat === step1Data.signatureFormat
      )?.value ?? 'PAdES'

    const signatureValidationError = validateFileForSignatureType(
      file,
      signatureTypeToValidate,
      t
    )
    if (signatureValidationError) {
      return signatureValidationError
    }

    return null
  }

  const [isLoading, setIsLoading] = useState(false)

  const validateFileForStep1 = (file: File): string | null => {
    // Lightweight validation for Step 1 (only basic checks)
    const validationContext = {
      signatureType: 'paidSignature' as const,
      currentDocuments: documents.map(doc => ({
        size: documentSizes.get(doc.id) ?? 0,
        name: doc.name,
        ...(doc.code !== undefined ? { code: doc.code } : {}),
      })),
      t,
    }

    return validateFileUpload(file, validationContext)
  }

  const handleNewProcessById = (processId: string) => {
    return getProcessById(processId, {
      onSuccess: processDetails => {
        const processData = processDetails as { expirationTime?: string }
        if (
          processData?.expirationTime &&
          getExpirationTime() !== processData.expirationTime
        ) {
          saveExpirationTime(processData.expirationTime)
          setExpirationTime(processData.expirationTime)
        }
      },
      onError: () => {
        // Failed to get process expiration time — ignore to avoid blocking upload
      },
    })
  }

  const handleAdd = async (data: Step1Data) => {
    if (!data) return

    setError(null)
    setInputCodeError(null)

    if (data.enableGraphicSignature && !signatureCoordinates) {
      setError(t('documentAdd.errors.graphicSignaturePositionRequired'))
      return
    }

    // If paid signature is selected, ensure signature code is provided
    if (data.signatureType === 'paidSignature' && !data.signatureCode) {
      setInputCodeError(t('documentAdd.errors.signatureCodeRequired'))
      return
    }

    const validationError = validateFile(data.file)
    if (validationError) {
      setError(validationError)
      return
    }

    let processId = getProcessId()
    const token = getTokenFromSession()

    setIsLoading(true)

    try {
      const isPaidSignature = data.signatureType === 'paidSignature'

      const signature = signatureCoordinates
        ? {
            signatureBottom: Math.round(signatureCoordinates.bottom),
            signatureLeft: Math.round(signatureCoordinates.left),
            signaturePage: signatureCoordinates.page,
            signatureWidth: Math.round(signatureCoordinates.width),
            signatureHeight: Math.round(signatureCoordinates.height),
          }
        : {}

      await addDocument(
        {
          file: data.file,
          name: data.file.name,
          documentSignFormat: isPaidSignature ? data.signatureFormat : 'PAdES',
          ...signature,
          ...(processId ? { processId } : {}),
          ...(data.signatureCode && isPaidSignature
            ? { code: data.signatureCode }
            : {}),
          ...(token ? { token } : {}),
        },
        {
          onSuccess: response => {
            // If we didn't have a processId before, save the one returned from API
            const responseData = response as { processId?: string }
            if (!processId && responseData?.processId) {
              const newProcessId = responseData.processId
              processId = newProcessId
              saveProcessId(newProcessId)

              handleNewProcessById(newProcessId)
            }

            const documentResponse = response as DocumentDto
            onAddDocuments(documentResponse.id, data.file.size)

            // Reset form
            setCurrentStep(1)
            setStep1Data(null)
            setSignatureCoordinates(null)
            onClose()
          },
          onError: error => {
            setError(
              error.response?.data?.detail ??
                t('documentAdd.errors.uploadFailed')
            )
          },
        }
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setError(null)
    setSignatureCoordinates(null)
    setCurrentStep(1)
  }

  const handleStep1Continue = async (data: Step1Data) => {
    setStep1Data(data)
    setError(null)
    setInputCodeError(null)

    const isPaidSignature = data.signatureType === 'paidSignature'
    if (isPaidSignature && data.signatureCode) {
      setIsLoading(true)
      try {
        await checkSignCode(data.signatureCode, {
          onSuccess: ({ valid }) => {
            if (!valid) {
              setInputCodeError(t('documentAdd.errors.invalidSignatureCode'))
              throw new Error('Invalid signature code')
            }
          },
          onError: error => {
            if (error.response?.status !== 200) {
              setInputCodeError(
                error.response?.data?.detail ??
                  t('documentAdd.errors.invalidSignatureCode')
              )
              throw error
            }
          },
        })
      } catch {
        setIsLoading(false)
        return
      } finally {
        setIsLoading(false)
      }
    }

    if (
      graphicSignatureEnabled &&
      data.enableGraphicSignature &&
      data.file.type === 'application/pdf'
    ) {
      setCurrentStep(2)
    } else {
      handleAdd(data)
    }
  }

  const handleCancel = () => {
    if (isLoading) return

    // Reset to step 1
    setCurrentStep(1)
    setStep1Data(null)
    setError(null)
    setInputCodeError(null)
    setSignatureCoordinates(null)
    onClose()
  }

  if (currentStep === 2 && step1Data) {
    return (
      <AddDocumentDialogStep2
        isOpen={isOpen}
        onClose={handleCancel}
        selectedFile={step1Data.file}
        onCoordinatesChange={setSignatureCoordinates}
        onBack={handleBack}
        onAdd={() => handleAdd(step1Data)}
        isLoading={isLoading}
        error={error}
        onErrorDismiss={() => setError(null)}
      />
    )
  }

  return (
    <AddDocumentDialogStep1
      isOpen={isOpen}
      onClose={handleCancel}
      isFreeSignatureEnabled={Boolean(isFreeSignatureEnabled)}
      graphicSignatureEnabled={graphicSignatureEnabled ?? true}
      validateFile={validateFileForStep1}
      onContinue={handleStep1Continue}
      initialData={step1Data}
      inputCodeError={inputCodeError}
      isLoading={isLoading}
      error={error}
      onErrorDismiss={() => setError(null)}
      onInputCodeChange={() => setInputCodeError(null)}
    />
  )
}
